import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { SvgIconType } from '@thermenresorts/icons';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { youtubeVideoId } from 'src/core/utils/Youtube';
import { useExerciseStore } from 'src/domain/stores/useExerciseStore';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import YoutubePlayerView from 'src/presentation/components/molecules/YoutubePlayerView';
import BottomSheet from 'src/presentation/components/organisms/BottomSheet';
import { exerciseMetaLine } from 'src/presentation/helpers/ExerciseMeta';

const ExerciseDialog = () => {
	const { selectedExercise, close } = useExerciseStore();
	const [playing, setPlaying] = useState(false);

	useEffect(() => setPlaying(false), [selectedExercise]);

	if (selectedExercise === null) {
		return null;
	}

	const videoId = youtubeVideoId(selectedExercise.url);
	const meta = exerciseMetaLine(selectedExercise);

	return (
		<BottomSheet visible={true} onClose={close}>
			{playing && videoId !== null ? (
				<View style={styles.media}>
					<YoutubePlayerView title={selectedExercise.title} videoId={videoId} />
				</View>
			) : (
				<Pressable
					accessibilityLabel={videoId === null ? undefined : Translations.playVideo}
					accessibilityRole={videoId === null ? undefined : 'button'}
					disabled={videoId === null}
					style={styles.media}
					onPress={() => setPlaying(true)}
				>
					<ThermenImage
						contentFit="cover"
						source={selectedExercise.media ? { uri: selectedExercise.media } : null}
						style={styles.poster}
					/>
					{videoId === null ? null : (
						<View style={styles.playBadge}>
							<SvgIcon name={SvgIconType.play} size={Dimens.iconSize18} tintColor={Colors.green700} />
						</View>
					)}
				</Pressable>
			)}
			<View style={styles.body}>
				{meta === null ? null : <Text style={styles.meta}>{meta}</Text>}
				<Text style={styles.title}>{selectedExercise.title}</Text>
				{selectedExercise.intro === null ? null : <Text style={styles.intro}>{selectedExercise.intro}</Text>}
			</View>
			{videoId === null ? null : (
				<PrimaryButton
					backgroundColor={Colors.green700}
					label={playing ? Translations.stopVideo : Translations.playVideo}
					onPress={() => setPlaying(!playing)}
				/>
			)}
		</BottomSheet>
	);
};

const styles = StyleSheet.create({
	media: {
		aspectRatio: Dimens.aspectRatio16By9,
		borderRadius: Dimens.borderRadius4,
		backgroundColor: Colors.gray100,
		overflow: 'hidden',
		alignItems: 'center',
		justifyContent: 'center',
	},
	poster: {
		width: '100%',
		height: '100%',
	},
	playBadge: {
		position: 'absolute',
		width: Dimens.size50,
		height: Dimens.size50,
		borderRadius: Dimens.borderRadiusPill,
		backgroundColor: Colors.white,
		alignItems: 'center',
		justifyContent: 'center',
		paddingLeft: Dimens.padding4,
	},
	body: {
		gap: Dimens.gap4,
	},
	meta: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize12,
		lineHeight: Dimens.lineHeight21,
		color: Colors.gray650,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize22,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray900,
	},
	intro: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize15,
		lineHeight: Dimens.lineHeight22,
		color: Colors.greenGray,
		marginTop: Dimens.gap10,
	},
});

export default ExerciseDialog;
